import CommentSection from '../../components/CommentSection/CommentSection';
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import newsService from '../../services/newsServices';
import analyticsService from '../../services/analytics';
import { Article } from '../../types/news.types';
import { getCategoryColor, getCategoryLabel } from '../../utils/categories';
import { formatDate, formatTimeAgo } from '../../utils/dateFormatter';
import styles from './NewsDetail.module.css';

const NewsDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<Article | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likes, setLikes] = useState(0);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    const fetchArticle = async () => {
      if (!slug) {
        setError('Article not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const startTime = performance.now();

        // Fetch main article
        const articleData = await newsService.getArticleBySlug(slug);
        setArticle(articleData);
        setLikes(articleData.likes);

        // Track article view
        analyticsService.trackArticleView(
          articleData.slug,
          articleData.headline,
          articleData.category
        );

        // Track article load time
        const loadTime = performance.now() - startTime;
        analyticsService.trackTiming('Article', 'load', Math.round(loadTime), articleData.slug);

        // Fetch related articles from same category
        if (articleData.category) {
          const related = await newsService.getArticlesByCategory(
            articleData.category,
            1,
            4
          );
          const filteredRelated = related.articles.filter((a: Article) => a.id !== articleData.id);
          setRelatedArticles(filteredRelated.slice(0, 3));
        }

        setLoading(false);
        window.scrollTo(0, 0);
      } catch (err: any) {
        console.error('Failed to fetch article:', err);
        setError(err.message || 'Article not found');
        
        // Track error
        analyticsService.trackError(
          err.message || 'Article fetch failed',
          `/news/${slug}`,
          err.stack
        );
        
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug]);

  const handleLike = async () => {
    if (!article) return;

    try {
      const result = await newsService.toggleLike(article.id);
      setLikes(result.likes);
      setIsLiked(result.isLiked);

      // Track like/unlike event
      if (result.isLiked) {
        analyticsService.trackArticleLike(article.slug, article.headline);
      } else {
        analyticsService.trackArticleUnlike(article.slug, article.headline);
      }
    } catch (err) {
      console.error('Failed to toggle like:', err);
    }
  };

  const handleShare = () => {
    if (!article) return;

    if (navigator.share) {
      navigator.share({
        title: article.headline,
        text: article.excerpt,
        url: window.location.href,
      }).then(() => {
        // Track share event
        analyticsService.trackArticleShare(article.slug, article.headline, 'native');
      }).catch(err => {
        console.log('Share failed:', err);
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
      
      // Track copy link event
      analyticsService.trackArticleShare(article.slug, article.headline, 'clipboard');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
          <p>Loading article...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !article) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <h1>404</h1>
          <p>{error || 'Article not found'}</p>
          <button className={styles.backButton} onClick={() => navigate('/')}>
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const featuredUrl = article.featuredImage?.url || '';

  return (
    <article className={styles.articlePage}>
      {/* Breadcrumb */}
      <div className={styles.breadcrumbContainer}>
        <div className={styles.container}>
          <nav className={styles.breadcrumb}>
            <Link to="/">Home</Link>
            <span className={styles.separator}>/</span>
            <Link to={`/category/${article.category}`}>
              {getCategoryLabel(article.category)}
            </Link>
            <span className={styles.separator}>/</span>
            <span className={styles.current}>{article.headline}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <header className={styles.articleHeader}>
        <div className={styles.container}>
          <div className={styles.headerContent}>
            <Link
              to={`/category/${article.category}`}
              className={styles.categoryBadge}
              style={{ backgroundColor: getCategoryColor(article.category) }}
            >
              {getCategoryLabel(article.category)}
            </Link>

            <h1 className={styles.headline}>{article.headline}</h1>

            {article.excerpt && <p className={styles.excerpt}>{article.excerpt}</p>}

            <div className={styles.meta}>
              <div className={styles.authorInfo}>
                {article.author?.profilePhoto ? (
                  <div className={styles.authorAvatar}>
                    <img
                      src={article.author?.profilePhoto}
                      alt={article.author?.name || 'Unknown'}
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  </div>
                ) : (
                  <div className={styles.authorAvatar} aria-hidden />
                )}
                <div className={styles.authorDetails}>
                  <span className={styles.authorName}>{article.author?.name || 'Unknown'}</span>
                  <div className={styles.articleMeta}>
                    <time dateTime={article.publishedAt || article.createdAt}>
                      {formatDate(article.publishedAt || article.createdAt)}
                    </time>
                    <span className={styles.separator}>•</span>
                    <span>{article.readTime} min read</span>
                    <span className={styles.separator}>•</span>
                    <span>{article.views.toLocaleString()} views</span>
                  </div>
                </div>
              </div>

              <div className={styles.actions}>
                <button
                  className={`${styles.likeButton} ${isLiked ? styles.liked : ''}`}
                  onClick={handleLike}
                  aria-label="Like article"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      fill={isLiked ? 'currentColor' : 'none'}
                    />
                  </svg>
                  <span>{likes}</span>
                </button>

                <button className={styles.shareButton} onClick={handleShare} aria-label="Share article">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
                    <path
                      d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>Share</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Featured Image */}
      {featuredUrl ? (
        <div className={styles.featuredImageContainer}>
          <div className={styles.container}>
            <figure className={styles.featuredImage} role="img" aria-label={article.featuredImage.alt || article.headline}>
              <img
                src={featuredUrl}
                alt={article.featuredImage.alt || article.headline}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
              {article.featuredImage.alt && (
                <figcaption>{article.featuredImage.alt}</figcaption>
              )}
            </figure>
          </div>
        </div>
      ) : null}

      {/* Article Content */}
      <div className={styles.articleBody}>
        <div className={styles.container}>
          <div className={styles.contentWrapper}>
            <div
              className={styles.content}
              dangerouslySetInnerHTML={{ __html: article.description || '' }}
            />

            {/* Tags */}
            {article.tags && article.tags.length > 0 && (
              <div className={styles.tags}>
                <h3>Tags:</h3>
                <div className={styles.tagList}>
                  {article.tags.map((tag) => (
                    <Link 
                      key={tag} 
                      to={`/search?q=${tag}`} 
                      className={styles.tag}
                      onClick={() => analyticsService.trackSearch(tag)}
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media and Comments */}
            <CommentSection
              articleTitle={article.headline}
              articleUrl={window.location.href}
              articleSlug={article.slug}
            />
          </div>
        </div>
      </div>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className={styles.relatedSection}>
          <div className={styles.container}>
            <h2 className={styles.relatedTitle}>Related Articles</h2>
            <div className={styles.relatedGrid}>
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/news/${related.slug}`}
                  className={styles.relatedCard}
                  onClick={() => {
                    analyticsService.trackArticleView(
                      related.slug,
                      related.headline,
                      related.category
                    );
                  }}
                >
                  <div className={styles.relatedImage}>
                    <img
                      src={related.featuredImage.thumbnailUrl || related.featuredImage.url}
                      alt={related.headline}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.relatedContent}>
                    <span
                      className={styles.relatedCategory}
                      style={{ color: getCategoryColor(related.category) }}
                    >
                      {getCategoryLabel(related.category)}
                    </span>
                    <h3 className={styles.relatedHeadline}>{related.headline}</h3>
                    <p className={styles.relatedTime}>
                      {formatTimeAgo(related.publishedAt || related.createdAt)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
};

export default NewsDetail;